import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell, { StatItem } from "../components/page-shell";
import DataTable, { TableRowData } from "../components/data-table";
import { invoicesTableColumns } from "../lib/table-columns";
import { DistributionBarGraph } from "../components/distribution-bar-graph";
import SankeyDiagram from "../components/sankey-diagram";
import SearchInput from "../components/search-input";
import InvoiceDrawer from "../components/invoice-drawer";
import {
  getInvoices,
  getInvoiceStatistics,
  updateInvoice,
  Invoice,
  InvoiceStatistics,
} from "../lib/api";
import { Modal } from "../components/ui/modal";
import { Edit2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] =
    useState<TableRowData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state for Mark as Paid
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markingInvoice, setMarkingInvoice] = useState<TableRowData | null>(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);

  // Reset pagination on tab changes (search handled separately).
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), reset pagination.
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);

  const queryClient = useQueryClient();
  const statusParam = currentTab === "All" ? undefined : currentTab;

  const invoicesQuery = useQuery({
    queryKey: ["invoices", { page: currentPage, limit: pageSize, search: searchTerm, status: statusParam }],
    queryFn: async () => getInvoices(currentPage, pageSize, searchTerm, statusParam),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<InvoiceStatistics>({
    queryKey: ["invoices", "statistics"],
    queryFn: async () => getInvoiceStatistics(),
  });

  useEffect(() => {
    if (!invoicesQuery.error) return;
    toast.error(getBackendErrorMessage(invoicesQuery.error, "Failed to fetch invoices"));
  }, [invoicesQuery.error]);

  useEffect(() => {
    if (!statisticsQuery.error) return;
    toast.error(getBackendErrorMessage(statisticsQuery.error, "Failed to fetch invoice statistics"));
  }, [statisticsQuery.error]);

  const invoices = invoicesQuery.data?.data ?? [];
  const statistics = statisticsQuery.data ?? null;
  const totalItems = invoicesQuery.data?.meta.total ?? 0;
  const totalPages = invoicesQuery.data?.meta.totalPages ?? 1;
  const initialLoading = invoicesQuery.isLoading;
  const tableLoading = invoicesQuery.isFetching && !invoicesQuery.isLoading;

  const markPaidMutation = useMutation({
    mutationFn: async (id: string) => updateInvoice(id, { status: "PAID" }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["invoices"] }),
        queryClient.invalidateQueries({ queryKey: ["invoices", "statistics"] }),
      ]);
    },
  });

  // Convert invoice data to table format (API filters by status)
  const tableData: TableRowData[] = useMemo(
    () =>
      invoices.map((invoice) => ({
        id: invoice.id,
        reference: invoice.reference,
        customer: invoice.customer?.fullName || "N/A",
        site: invoice.site?.name || "N/A",
        amount: `${invoice.currencyType}  ${parseFloat(invoice.amount || "0").toLocaleString()}`,
        totalAmount: `${invoice.currencyType}  ${parseFloat(invoice.totalAmount || "0").toLocaleString()}`,
        status: invoice.status,
        dueDate: invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) : "N/A",
        issueDate: invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }) : "N/A",
      })),
    [invoices],
  );

  // Get statistics display values
  const statsCards: StatItem[] = [
    {
      label: "Total Invoices",
      value: statistics?.totalInvoices.toString() || "0",
      description: `TZS ${statistics?.totals?.totalAmount?.toLocaleString() || "0"}`,
      icon: <Icons.Quotes className="w-5 h-5 text-primary-600" />,
    },
    {
      label: "Paid",
      value: statistics?.totals.paid.toLocaleString() || "0",
      description: `TZS ${statistics?.totals.paid.toLocaleString() || "0"}`,
      icon: <Icons.Billing className="w-5 h-5 text-primary-600" />,
    },
    {
      label: "Unpaid",
      value: statistics?.totals.unpaid.toLocaleString() || "0",
      description: `TZS ${statistics?.totals.unpaid.toLocaleString() || "0"}`,
      icon: <Icons.Deliveries className="w-5 h-5 text-primary-600" />,
    },
  ];

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedInvoiceDetails(row);
      },
    },
    {
      label: "Copy to new invoice",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { duplicateInvoiceId: row.id } });
      },
    },
    {
      label: "Copy to new quotation",
      icon: Icons.Copy,
      onClick: (row: TableRowData) => {
        navigate("/create-quotation", { state: { duplicateInvoiceId: row.id } });
      },
    },
    {
      label: "Edit",
      icon: Edit2,
      visible: (row: TableRowData) => String(row.status).toUpperCase() !== "PAID",
      onClick: (row: TableRowData) => {
        navigate("/create-invoice", { state: { invoiceId: row.id } });
      },
    },
    {
      label: "Mark as Paid",
      icon: Icons.Check,
      visible: (row: TableRowData) => String(row.status).toUpperCase() !== "PAID",
      onClick: (row: TableRowData) => {
        setMarkingInvoice(row);
        setShowMarkPaidModal(true);
      },
    },
  ];

  const handleConfirmMarkPaid = async () => {
    if (!markingInvoice) return;
    setIsMarkingPaid(true);
    try {
      await markPaidMutation.mutateAsync(markingInvoice.id as string);
      setShowMarkPaidModal(false);
      setMarkingInvoice(null);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to mark invoice as paid"));
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["invoices"] }),
      queryClient.invalidateQueries({ queryKey: ["invoices", "statistics"] }),
    ]);

  const openNewInvoice = () => {
    navigate("/create-invoice");
  };

  return (
    <>
      <InvoiceDrawer
        selectedInvoiceDetails={selectedInvoiceDetails}
        invoiceDetails={selectedInvoiceDetails}
        onClose={() => {
          setSelectedInvoiceDetails(null);
        }}
      />

      <Modal
        isOpen={showMarkPaidModal}
        onClose={() => setShowMarkPaidModal(false)}
        title="Confirm Mark as Paid"
        description={markingInvoice ? `Are you sure you want to mark invoice ${markingInvoice.reference || markingInvoice.id} as PAID?` : "Are you sure?"}
      >
        <div className="p-6">
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowMarkPaidModal(false)}>
              Cancel
            </Button>
            <Button className="bg-primary-600 text-white" onClick={handleConfirmMarkPaid} disabled={isMarkingPaid}>
              {isMarkingPaid ? "Processing..." : "Confirm Mark as Paid"}
            </Button>
          </div>
        </div>
      </Modal>

      <PageShell
        title="Invoices"
        description="Stay on top of your rental payments, invoices, and billing history."
        loading={initialLoading}
        toolbar={
          <div className="flex flex-wrap items-center gap-3">
            <RefreshToolbarButton
              isRefreshing={invoicesQuery.isFetching || statisticsQuery.isFetching}
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewInvoice}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </div>
        }
        stats={statsCards}
        insights={
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <DistributionBarGraph
              title="Invoices by Months"
              data={
                statistics?.monthlyTrend?.slice(0, 3).map((trend) => ({
                  label: trend.month,
                  value: trend.count,
                })) || [
                  { label: "January", value: 10 },
                  { label: "February", value: 15 },
                  { label: "March", value: 20 },
                ]
              }
            />
            <SankeyDiagram
              title="Invoices by Status"
              data={{
                nodes: [
                  {
                    name: "All Invoices",
                    percent: 100,
                    absoluteValue: statistics?.totalInvoices || 0,
                  },
                  ...((statistics?.byStatus || []).map((status, index) => ({
                    name: status.status,
                    percent:
                      (status.count / (statistics?.totalInvoices || 1)) *
                      100,
                    absoluteValue: status.count,
                  })) || []),
                ],
                links: [
                  ...((statistics?.byStatus || []).map((status, index) => ({
                    source: 0,
                    target: index + 1,
                    value: status.count,
                    sourceName: "All Invoices",
                  })) || []),
                ],
              }}
            />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Invoice", icon: Icons.Plus, onClick: openNewInvoice },
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabBar>
            {[
              { label: "All", count: statistics?.totalInvoices || 0 },
              ...(statistics?.byStatus?.map((s) => ({ label: s.status, count: s.count })) || []),
            ].map((tab) => (
              <TabButton
                key={tab.label}
                label={tab.label}
                count={tab.count}
                active={currentTab === tab.label}
                onClick={() => {
                  setCurrentTab(tab.label);
                }}
              />
            ))}
          </TabBar>
        </div>
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={(value) => setSearchInput(value)}
          placeholder="Search invoices..."
        />
        <DataTable
          columns={invoicesTableColumns}
          data={tableData}
          actions={actions}
          loading={tableLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Invoices;
